import { useId } from "react";

import type { FacetFilterBarProps, FacetTone } from "./types";

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

const toneClass: Record<FacetTone, string> = {
  neutral: "border-[var(--line3)] bg-[var(--chip)] text-[var(--muted)]",
  "severity-blocker": "border-[#E9C7C0] bg-[#F6E7E4] text-[#BC4A3C]",
  "severity-warning": "border-[#E9D3AD] bg-[#F6EEDD] text-[#8A6A2E]",
  "severity-info": "border-[#D7E1EA] bg-[#E2ECF4] text-[#3E6E9A]",
  "status-included": "border-[#D9DED5] bg-[#EAF1E6] text-[#4E7644]",
  "status-by_others": "border-[#DCE2E8] bg-[#ECEFF3] text-[#5E6E82]",
  "status-excluded": "border-[var(--line3)] bg-[var(--line2)] text-[var(--muted2)]"
};

const selectedToneClass: Record<FacetTone, string> = {
  neutral: "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]",
  "severity-blocker": "border-[#BC4A3C] bg-[#BC4A3C] text-white",
  "severity-warning": "border-[#A8823E] bg-[#A8823E] text-white",
  "severity-info": "border-[#3E6E9A] bg-[#3E6E9A] text-white",
  "status-included": "border-[#4E7644] bg-[#4E7644] text-white",
  "status-by_others": "border-[#5E6E82] bg-[#5E6E82] text-white",
  "status-excluded": "border-[var(--muted2)] bg-[var(--muted2)] text-white"
};

export function FacetFilterBar({
  facets,
  selection,
  onToggle,
  onClearAll,
  groupBy = null,
  groupByOptions,
  onGroupByChange
}: FacetFilterBarProps) {
  const groupById = useId();
  const hasSelection = Object.values(selection).some((values) => values.length > 0);
  const canGroup = !!onGroupByChange && !!groupByOptions && groupByOptions.length > 0;
  const visibleFacets = facets.filter((facet) => facet.values.length > 1);
  const hasVisibleFacets = visibleFacets.length > 0;
  const hasControls = (hasSelection && !!onClearAll) || canGroup;

  if (!hasVisibleFacets && !hasControls) {
    return null;
  }

  return (
    <div className="rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-3 py-3 shadow-[0_1px_2px_rgba(35,33,30,.04)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {hasVisibleFacets ? (
          <div className="flex min-w-0 flex-1 flex-wrap gap-3">
            {visibleFacets.map((facet) => {
              const selectedValues = selection[facet.key] ?? [];

              return (
                <div key={facet.key} className="min-w-0">
                  <div className="mb-1.5 text-[10px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
                    {facet.label}
                  </div>
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="group"
                    aria-label={facet.label}
                  >
                    {facet.values.map((option) => {
                      const selected = selectedValues.includes(option.value);
                      const zeroCount = option.count === 0;
                      const tone = option.tone ?? "neutral";

                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => onToggle(facet.key, option.value)}
                          className={cx(
                            "inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] font-extrabold leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--ink)] focus:ring-offset-2 focus:ring-offset-[var(--surface)]",
                            selected ? selectedToneClass[tone] : toneClass[tone],
                            !selected && zeroCount && "opacity-45"
                          )}
                        >
                          <span>{option.label}</span>
                          <span
                            className={cx(
                              "tabular-nums",
                              selected ? "text-current" : "text-[var(--muted2)]"
                            )}
                          >
                            · {option.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {hasControls ? (
          <div className="flex flex-none flex-wrap items-center justify-end gap-2">
            {hasSelection && onClearAll ? (
              <button
                type="button"
                onClick={onClearAll}
                className="min-h-8 cursor-pointer rounded-full border border-[var(--line3)] bg-[var(--surface)] px-3 text-[11.5px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)] focus:outline-none focus:ring-2 focus:ring-[var(--ink)] focus:ring-offset-2 focus:ring-offset-[var(--surface)]"
              >
                Clear
              </button>
            ) : null}

            {canGroup ? (
              <label className="flex items-center gap-2 text-[11.5px] font-bold text-[var(--muted)]">
                <span>Group by</span>
                <select
                  id={groupById}
                  value={groupBy ?? ""}
                  onChange={(event) =>
                    onGroupByChange(event.currentTarget.value || null)
                  }
                  className="min-h-8 cursor-pointer rounded-[9px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 text-[11.5px] font-extrabold text-[var(--ink2)] outline-none hover:bg-[var(--chip)] focus:border-[var(--ink)]"
                >
                  {groupByOptions.map((option) => (
                    <option key={option.value ?? "__none__"} value={option.value ?? ""}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
